import { useState, useMemo } from 'react';
import { 
  Clock, 
  Calendar,
  Filter,
  Download,
  MessageSquare,
  TrendingUp,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Task, TimeLog, User } from '../types';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike, toDateKey } from '../utils/dateUtils';
import { SummaryStatCard } from '../components/StatCards';
import SurfaceCard from '../components/SurfaceCard';
import ReportSectionCard from '../components/ReportSectionCard.tsx';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { WIDGET_MUTED_TEXT_CLASS } from '../constants/widgetTheme';

export default function DeveloperTimesheet() {
  const { state, loading } = useApp();
  const { projects, tasks } = useOrganizationData(state, {
    includeAllOrganizations: true,
  });
  const currentUser = state?.currentUser ?? null;
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [viewMode, setViewMode] = useState<'time' | 'standup'>('time');
  const [groupBy, setGroupBy] = useState<'all' | 'project' | 'organization'>('all');

  const toTime = (value: unknown, fallback: number = 0): number => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.getTime() : fallback;
  };

  const formatDate = (
    value: unknown,
    options?: Intl.DateTimeFormatOptions,
    fallback: string = 'Unknown date'
  ): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleDateString('en-US', options) : fallback;
  };

  const formatTime = (
    value: unknown,
    options?: Intl.DateTimeFormatOptions,
    fallback: string = 'Unknown time'
  ): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleTimeString('en-US', options) : fallback;
  };

  // Get all time logs and standup notes for current user in selected month
  const myTimeLogs = useMemo(() => {
    if (!currentUser) {
      return [] as Array<TimeLog & { task: Task; projectName: string; organizationName: string }>;
    }

    const [year, month] = selectedMonth.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    const logs: Array<TimeLog & { task: Task; projectName: string; organizationName: string }> = [];
    
    tasks.forEach(task => {
      const taskProject = projects.find(p => p.id === task.projectId);
      (task.timeLogs || [])
        .filter(log => {
          if (log.userId !== currentUser?.id) return false;
          const logDate = parseDateLike(log.date);
          if (!logDate) return false;
          return logDate >= startDate && logDate <= endDate;
        })
        .forEach(log => {
          logs.push({
            ...log,
            task,
            projectName: taskProject?.name || 'Unknown Project',
            organizationName: taskProject?.organizationId || 'Unknown Org'
          });
        });
    });

    return logs.sort((a, b) => toTime(b.date) - toTime(a.date));
  }, [tasks, currentUser?.id, selectedMonth, projects]);

  const myStandupNotes = useMemo(() => {
    if (!currentUser) {
      return [] as Array<{ note: any; task: Task; projectName: string; organizationName: string }>;
    }

    const [year, month] = selectedMonth.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    const notes: Array<{ note: any; task: Task; projectName: string; organizationName: string }> = [];
    
    tasks.forEach(task => {
      const taskProject = projects.find(p => p.id === task.projectId);
      (task.standupNotes || [])
        .filter(note => {
          if (note.authorId !== currentUser.id) return false;
          const noteDate = parseDateLike(note.createdAt);
          if (!noteDate) return false;
          return noteDate >= startDate && noteDate <= endDate;
        })
        .forEach(note => {
          notes.push({
            note,
            task,
            projectName: taskProject?.name || 'Unknown Project',
            organizationName: taskProject?.organizationId || 'Unknown Org'
          });
        });
    });

    return notes.sort((a, b) => toTime(b.note.createdAt) - toTime(a.note.createdAt));
  }, [tasks, currentUser?.id, selectedMonth, projects]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalHours = myTimeLogs.reduce((sum, log) => sum + log.hours, 0);
    const totalDays = new Set(
      myTimeLogs
        .map(log => toDateKey(log.date))
        .filter((dateKey): dateKey is string => !!dateKey)
    ).size;
    const totalTasks = new Set(myTimeLogs.map(log => log.task.id)).size;
    const avgHoursPerDay = totalDays > 0 ? totalHours / totalDays : 0;

    return { totalHours, totalDays, totalTasks, avgHoursPerDay };
  }, [myTimeLogs]);

  // Group data
  const groupedData = useMemo(() => {
    if (groupBy === 'all') {
      return { 'All Work': myTimeLogs };
    } else if (groupBy === 'project') {
      const grouped: Record<string, typeof myTimeLogs> = {};
      myTimeLogs.forEach(log => {
        if (!grouped[log.projectName]) grouped[log.projectName] = [];
        grouped[log.projectName].push(log);
      });
      return grouped;
    } else {
      const grouped: Record<string, typeof myTimeLogs> = {};
      myTimeLogs.forEach(log => {
        if (!grouped[log.organizationName]) grouped[log.organizationName] = [];
        grouped[log.organizationName].push(log);
      });
      return grouped;
    }
  }, [myTimeLogs, groupBy]);

  if (loading || !state || !currentUser) {
    return <div className={`p-8 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>Loading timesheet...</div>;
  }

  const handleExport = () => {
    // Export to CSV
    const headers = ['Date', 'Project', 'Task', 'Description', 'Hours'];
    const rows = myTimeLogs.map(log => [
      formatDate(log.date),
      log.projectName,
      log.task.title,
      log.description,
      log.hours.toString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-${selectedMonth}.csv`;
    a.click();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-2xl font-semibold text-foreground">My Timesheet</h1>
              <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{currentUser.name}</p>
            </div>
          </div>
          <Button onClick={handleExport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Controls */}
        <SurfaceCard className="mt-4 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Calendar className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
              <input
                type="date"
                title="Select timesheet month"
                aria-label="Select timesheet month"
                value={`${selectedMonth}-01`}
                onChange={(e) => setSelectedMonth(e.target.value.slice(0, 7))}
                className={APP_NATIVE_FIELD_CLASS}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'time' ? 'default' : 'outline'}
                onClick={() => setViewMode('time')}
              >
                Time Logs
              </Button>
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'standup' ? 'default' : 'outline'}
                onClick={() => setViewMode('standup')}
              >
                Standup Notes
              </Button>
            </div>

            {viewMode === 'time' && (
              <div className="flex items-center gap-2 sm:ml-auto">
                <Filter className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
                <select
                  title="Group time logs"
                  aria-label="Group time logs"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as any)}
                  className={APP_NATIVE_FIELD_CLASS}
                >
                  <option value="all">All Together</option>
                  <option value="project">By Project</option>
                  <option value="organization">By Organization</option>
                </select>
              </div>
            )}
          </div>
        </SurfaceCard>
      </div>

      <div>
        {viewMode === 'time' ? (
          <>
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <SummaryStatCard
                icon={<Clock className="w-5 h-5" />}
                label="Total Hours"
                value={`${stats.totalHours.toFixed(1)}h`}
                color="blue"
              />
              <SummaryStatCard
                icon={<Calendar className="w-5 h-5" />}
                label="Days Worked"
                value={stats.totalDays.toString()}
                color="green"
              />
              <SummaryStatCard
                icon={<Target className="w-5 h-5" />}
                label="Tasks Worked"
                value={stats.totalTasks.toString()}
                color="purple"
              />
              <SummaryStatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Avg Hours/Day"
                value={`${stats.avgHoursPerDay.toFixed(1)}h`}
                color="orange"
              />
            </div>

            {/* Time Logs */}
            <div className="space-y-6">
              {Object.entries(groupedData).map(([groupName, logs]) => {
                const groupHours = logs.reduce((sum, log) => sum + log.hours, 0);
                
                return (
                  <ReportSectionCard
                    key={groupName}
                    title={groupName}
                    headerRight={`${groupHours.toFixed(1)} hours`}
                  >
                    <div className="divide-y divide-border">
                      {logs.map((log) => (
                        <div key={log.id} className="p-4 hover:bg-accent/20">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                                  {formatDate(log.date, { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </span>
                                {groupBy !== 'project' && (
                                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                                    {log.projectName}
                                  </span>
                                )}
                              </div>
                              <div className="font-medium mb-1">{log.task.title}</div>
                              <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{log.description}</p>
                            </div>
                            <div className="ml-4 flex items-center gap-2">
                              <span className="px-3 py-1 bg-chart-2/10 text-chart-2 rounded-full font-semibold">
                                {log.hours}h
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ReportSectionCard>
                );
              })}

              {myTimeLogs.length === 0 && (
                <SurfaceCard className="p-12 text-center">
                  <Clock className="mx-auto mb-4 h-12 w-12 text-muted-foreground/70" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">No time logs for this month</h3>
                  <p className={WIDGET_MUTED_TEXT_CLASS}>Start logging your time to see it here</p>
                </SurfaceCard>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Standup Notes */}
            <ReportSectionCard title="My Standup Notes" subtitle={`${myStandupNotes.length} notes this month`}>
              <div className="divide-y divide-border">
                {myStandupNotes.map(({ note, task, projectName }) => (
                  <div key={note.id} className="p-4 hover:bg-accent/20">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-primary" />
                        <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                          {formatDate(note.createdAt, { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                          {formatTime(note.createdAt, { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                        {projectName}
                      </span>
                    </div>
                    <div className="font-medium mb-2 text-sm">{task.title}</div>
                    <p className="whitespace-pre-wrap text-foreground/90">{note.content}</p>
                  </div>
                ))}
              </div>

              {myStandupNotes.length === 0 && (
                <div className="p-12 text-center">
                  <MessageSquare className="mx-auto mb-4 h-12 w-12 text-muted-foreground/70" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">No standup notes for this month</h3>
                  <p className={WIDGET_MUTED_TEXT_CLASS}>Add standup notes to track your daily progress</p>
                </div>
              )}
            </ReportSectionCard>
          </>
        )}
      </div>
    </div>
  );
}